<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <div v-if="meters.length" class="screen__cards screen__stretch pretty-scrollbar">
      <MeterCard
        v-for="meter in meters"
        :key="`screen_card_${meter.id}`"
        :meter="meter"
        :readonly="isExpired"
        @submit="trySendInfo"
      />
    </div>
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      class="screen__overlay"
      slot="root"
      @disagree="hideAbortModal"
      @agree="sendInfo"
    />
  </BaseScreen>
</template>

<script>
import { mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';

import MeterCard from '@/components/MeterCard.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';

export default {
  name: 'PersonalMeterScreen',
  mixins: [screenMixin],
  components: {
    MeterCard,
    ModalConfirm,
  },
  data() {
    return {
      meters: [],
      isFetched: false,
      isDateChecked: false,
      isAbortModalVisible: false,
    };
  },
  computed: {
    titleText() {
      return this.$t('titles.meters');
    },
    pendingText() {
      return this.isFetched
        ? this.$t('pending.meter')
        : this.$t('pending.fetch');
    },
    screenCase() {
      if (this.isThanksVisible) {
        return CONSTANTS.STEP_METER_SENDED;
      }
      if (this.isErrorVisible) {
        if (this.isExpired && !this.isDateChecked) {
          return CONSTANTS.STEP_BAD_DATE;
        }
        if (this.isMetersEmpty) {
          return CONSTANTS.STEP_METERS_EMPTY;
        }
        return this.isFetched
          ? CONSTANTS.STEP_SEND_FAILED
          : CONSTANTS.STEP_CONNECTION_ERROR;
      }
      if (this.isFetched && !this.isExpired && !this.isPending) {
        return this.isAbortModalVisible
          ? CONSTANTS.STEP_CONFIRM_SEND
          : CONSTANTS.STEP_SEND_METER;
      }
      return null;
    },
    retryAction() {
      return this.isFetched ? this.sendInfo : this.prefetch;
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.thanks'),
          size: 'l',
        },
        description: {
          text: this.$t(this.isFullInfo ? 'texts.anotherService' : 'texts.meterInfoWasSended'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          this.isFullInfo ? {
            text: this.$t('actions.backToPersonal'),
            action: this.goToPersonalScreen,
          } : {
            text: this.$t('actions.sendMore'),
            action: this.sendMore,
          },
        ],
      };
    },
    error() {
      if (this.isExpired && !this.isDateChecked) {
        return this.expired;
      }
      if (this.isMetersEmpty) {
        return this.emptyError;
      }
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t(this.isFetched ? 'texts.sendInfoFailed' : 'texts.badConnection'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
    emptyError() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.emptyMeters'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.emptyMeters'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
        ],
      };
    },
    expired() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.sendInfoNotPossible'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.sendInfoNotPossible', {
            minDay: CONSTANTS.SEND_METER_MIN_DAY,
            maxDay: CONSTANTS.SEND_METER_MAX_DAY,
          }),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          // {
          //   text: this.$t('actions.viewPrevious'),
          //   action: this.viewPrevious,
          // },
        ],
      };
    },
    abortModal() {
      return {
        title: {
          text: this.$t('titles.sendEmptyInfo'),
          size: 'l',
          isNormal: true,
          isCenter: true,
        },
        agreeText: this.$t('actions.yesSendInfo'),
        disagreeText: this.$t('actions.noAbortSending'),
        isInversed: true,
      };
    },
    isMetersEmpty() {
      return this.isFetched && this.meters.length === 0;
    },
    isFullInfo() {
      return this.meters.every((el) => el.isComplete());
    },
    hasInfo() {
      return this.meters.some((el) => el.isComplete());
    },
    isExpired() {
      const currentDay = new Date().getDate();
      return currentDay < CONSTANTS.SEND_METER_MIN_DAY
      || currentDay > CONSTANTS.SEND_METER_MAX_DAY;
    },
  },
  created() {
    this.checkDate();
    this.initActions([
      [CONSTANTS.ACTION_DISAGREE, this.hideAbortModal],
      [CONSTANTS.ACTION_AGREE, this.sendInfo],
      [CONSTANTS.ACTION_SUBMIT, this.trySendInfo],
      // [CONSTANTS.ACTION_CONTINUE, this.viewPrevious],
      [CONSTANTS.ACTION_BACK, this.goToPersonalScreen],
      [CONSTANTS.ACTION_MORE, this.sendMore],
    ]);
  },
  methods: {
    ...mapActions('personal', [
      'fetchAccountsMeters',
      'sendAllMeters',
    ]),
    checkDate() {
      return this.isExpired ? this.showError() : this.prefetch();
    },
    sendMore() {
      this.hideThanks();
    },
    // viewPrevious() {
    //   this.isDateChecked = true;
    //   this.hideError();
    //   this.prefetch();
    // },
    async prefetch() {
      try {
        this.showPending(0);
        this.meters = await this.fetchAccountsMeters();
        this.isFetched = true;
        if (this.meters.length) {
          this.hidePending();
        } else {
          this.showError();
        }
      } catch (e) {
        console.error('fetchAccountsMeters', e);
        this.showError();
      }
    },
    async sendInfo() {
      try {
        this.showPending();
        this.hideAbortModal();
        await this.sendAllMeters(this.meters);
        this.showThanks();
      } catch (e) {
        console.error('sendInfo', e);
        this.showError();
      }
    },
    trySendInfo() {
      if (!this.hasInfo) {
        return;
      }
      if (this.isFullInfo) {
        this.sendInfo();
        return;
      }
      this.showAbortModal();
    },
    goToPersonalScreen() {
      this.$router.push(this.$router.options.paths.personal);
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
  },
};
</script>
